import { Menu, PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import useSWR from 'swr'

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { useSidebar } from '@/components/ui/sidebar'
import { usePageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { useRoleCheck } from '@/hooks/useRoleCheck'
import { Settings } from '@/interfaces/Settings'
import { useAuth } from '@/lib/auth'
import { LOGO_URL } from '@/lib/brand'
import { cn } from '@/lib/utils'
import { Role } from '@/types/User'

import { AccountSwitch } from './AccountSwitch'
import { Notifications } from './Notifications'
import { CommandPalette } from './shell/CommandPalette'
import { CreateMenu } from './shell/CreateMenu'

const sliceString = (str: string, length: number) => {
  if (str.length > length) {
    return str.slice(0, length) + '...'
  }

  return str
}

export function SiteHeader() {
  const { breadcrumbs, newButton } = usePageHeader()
  const { toggleSidebar, state, isMobile } = useSidebar()
  const { t } = useTranslation()
  const canCreate = useRoleCheck([Role.owner, Role.manager, Role.employee])
  const { user } = useAuth({ middleware: 'auth', redirectTo: '/auth/login', tmp: 'SiteHeader' })
  const isAdmin = !!user?.roles.includes(Role.admin)
  const { data: settings } = useSWR<Settings>(user && !isAdmin ? '/api/v2/settings' : null, apiFetcher)
  const features = settings?.features as string[] | undefined
  const [paletteOpen, setPaletteOpen] = useState(false)

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        setPaletteOpen((o) => !o)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  return (
    <header className="bg-background sticky top-0 z-50 flex w-full items-center border-b">
      <div className="flex h-(--header-height) w-full items-center gap-2 pr-4">
        {isMobile ? (
          // Na mobilu je menu vysouvací panel: hamburger vlevo, vedle logo
          <div className="flex h-full shrink-0 items-center gap-1 pl-2">
            <Button
              variant="ghost"
              size="icon"
              className="size-10 text-foreground"
              onClick={toggleSidebar}
              aria-label="Otevřít menu"
            >
              <Menu className="size-5" />
            </Button>
            <Link to="/" aria-label="invo.space, přehled" className="flex items-center gap-2 rounded-md p-1 hover:bg-muted">
              <img src={LOGO_URL} alt="" className="size-6" />
              <span className="text-sm font-medium tracking-[-0.01em]">invo.space</span>
            </Link>
          </div>
        ) : state === 'collapsed' ? (
          <div className="flex h-full shrink-0 items-center gap-2 pl-3">
            <button
              type="button"
              onClick={toggleSidebar}
              aria-label="Rozbalit menu"
              title="Rozbalit menu"
              className="group/logo relative grid size-8 place-items-center rounded-md hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
            >
              <img
                src={LOGO_URL}
                alt=""
                className="size-6 transition-opacity duration-150 group-hover/logo:opacity-0 group-focus-visible/logo:opacity-0"
              />
              <PanelLeftOpen className="absolute size-4 text-muted-foreground opacity-0 transition-opacity duration-150 group-hover/logo:opacity-100 group-focus-visible/logo:opacity-100" />
            </button>
            <Separator orientation="vertical" className="mr-2 h-4" />
          </div>
        ) : (
          <div className="flex h-full shrink-0 items-center gap-1 pr-2 pl-3 md:w-(--sidebar-width) md:border-r md:border-sidebar-border">
            <Link
              to="/"
              aria-label="invo.space, přehled"
              className="flex items-center gap-2 rounded-md py-1 pr-2 pl-1 hover:bg-muted"
            >
              <img src={LOGO_URL} alt="" className="size-6" />
              <span className="text-sm font-medium tracking-[-0.01em]">invo.space</span>
            </Link>
            <Button
              className="ml-auto size-8 text-muted-foreground"
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              aria-label="Sbalit menu"
              title="Sbalit menu"
            >
              <PanelLeftClose />
            </Button>
          </div>
        )}
        {breadcrumbs && (
          <Breadcrumb className="hidden min-w-0 md:block">
            <BreadcrumbList>
              {breadcrumbs.map((breadcrumb, index) => {
                const isLast = index === breadcrumbs.length - 1
                const isLink = breadcrumb.href && !isLast

                return (
                  <React.Fragment key={index}>
                    <BreadcrumbItem role="presentation">
                      {!isLast && (
                        <BreadcrumbLink
                          href={isLink ? breadcrumb.href : undefined}
                        >
                          {typeof breadcrumb.children === 'string' &&
                            sliceString(breadcrumb.children, 32)}
                          {typeof breadcrumb.children !== 'string' &&
                            breadcrumb.children}
                        </BreadcrumbLink>
                      )}
                      {isLast && (
                        <BreadcrumbPage>{breadcrumb.children}</BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                    {!isLast && <BreadcrumbSeparator />}
                  </React.Fragment>
                )
              })}
            </BreadcrumbList>
          </Breadcrumb>
        )}

        <button
          type="button"
          onClick={() => setPaletteOpen(true)}
          className="ml-auto hidden h-8 w-64 items-center gap-2 rounded-md border border-input px-2.5 text-[13px] text-muted-foreground transition-colors hover:bg-muted md:flex"
        >
          <Search className="size-4" />
          <span className="flex-1 text-left">{t('shell.search', 'Hledat...')}</span>
          <kbd className="rounded border px-1 text-[11px] tabular">⌘K</kbd>
        </button>
        <div className="ml-auto flex items-center gap-1 md:ml-3 md:gap-2">
          <Button
            variant="ghost"
            size="icon"
            className="size-9 text-muted-foreground md:hidden"
            onClick={() => setPaletteOpen(true)}
            aria-label="Hledat"
          >
            <Search className="size-5" />
          </Button>
          <Notifications />
          <AccountSwitch />
          {canCreate && !isAdmin && <CreateMenu primary={newButton} features={features} />}
        </div>
        <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} features={features} />
        {/* <SearchForm className="w-full sm:ml-auto sm:w-auto" /> */}
      </div>
    </header>
  )
}
